import { useNavigate, generatePath } from "react-router-dom";
import { useQuery } from "react-query";
import { Head, Spinner, generateBreadcrumbsData } from "component-library";
import { useTranslation } from "i18n";
import PageWrapperWithinLayout from "app-shell/PageWrapperWithinLayout/PageWrapperWithinLayout";
import { checkIsPerformer } from "shared-auth";
import usePagePermission from "app/hooks/usePagePermission";
import { useReturnUrl } from "app/hooks/useReturnUrl";
import UserAccountService from "services/api/UserAccount/UserAccountService";
import {
  IAddress,
  getRecurlyDetails,
  IBillingApiResponse,
} from "services/api/BillingApi/BillingApi";
import {
  BASE_PATH,
  CHECKOUT_FULL_PATH,
  MY_DETAILS_FULL_PATH,
  MY_LOGIN_DETAILS_FULL_PATH,
  MY_SUBSCRIPTION_FULL_PATH,
  CheckoutType,
} from "config/common";
import {
  IAccountSettingsLoginDetailsProps,
  IAccountSettingsLoginDetailsSecurityQ,
} from "models/page";
import checkIsNoActiveSubscription from "helpers/checkIsNoActiveSubscription";
import checkIsValidAddress from "helpers/checkIsValidAddress";
import { useUpdateDetailsPopUp } from "app/hooks/useUpdateDetailsPopUp";
import { BreadcrumbSection, breadcrumbs } from "app/constants";
import { FieldNames } from "./MyDetails/constants";
import SettingSection from "components/SettingSection/SettingSection";

const AccountSettings = () => {
  usePagePermission([checkIsPerformer], BASE_PATH);
  const { redirectWithReturnUrl } = useReturnUrl();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: fetchedSettingsData, isLoading } = useQuery<
    [
      IAccountSettingsLoginDetailsProps | undefined,
      IAccountSettingsLoginDetailsSecurityQ | undefined,
      IBillingApiResponse | undefined,
    ]
  >(
    "account-settings",
    async () =>
      await Promise.all([
        UserAccountService.getUserDetailsForSettingsPage(),
        UserAccountService.getUserSecurityQForSettingsPage(),
        getRecurlyDetails(),
      ])
  );
  const allowedMemberGroup = checkIsPerformer();

  const [userDetails, userSecurityQ, recurlyDetails] =
    fetchedSettingsData ?? [];

  const isValidAddress = checkIsValidAddress(recurlyDetails?.address);
  const reactivationLink = generatePath(CHECKOUT_FULL_PATH, {
    type: CheckoutType.Reactivation,
  });

  const { showUpdateDetailsPopUp } = useUpdateDetailsPopUp(() =>
    redirectWithReturnUrl(MY_DETAILS_FULL_PATH, reactivationLink)
  );

  if (isLoading) return <Spinner />;

  const getAddressValue = () => {
    const address: IAddress | undefined = recurlyDetails?.address;

    if (!address) return "-";

    const addressLine1 = address[FieldNames.street1] ?? "";
    const addressLine2 = address[FieldNames.street2]
      ? `, ${address[FieldNames.street2]}`
      : "";
    const city = address[FieldNames.city]
      ? `, ${address[FieldNames.city]}`
      : "";
    const postalCode = address[FieldNames.postalCode]
      ? `, ${address[FieldNames.postalCode]}`
      : "";
    const region = address[FieldNames.region]
      ? `, ${address[FieldNames.region]}`
      : "";
    const country = address[FieldNames.country]
      ? `, ${address[FieldNames.country]}`
      : "";

    const fullAddress = `${addressLine1}${addressLine2}${city}${postalCode}${region}${country}`;

    return fullAddress || "-";
  };

  const noActiveSubscription = checkIsNoActiveSubscription({
    id: recurlyDetails?.account?.id,
    subscription: recurlyDetails?.subscription,
  });
  const showSubscriptionBlock =
    allowedMemberGroup &&
    (noActiveSubscription || recurlyDetails?.subscription);

  return (
    <>
      <Head>
        <title>{t("performer:accountSettings.pageTitle")}</title>
      </Head>
      <PageWrapperWithinLayout
        pageHeader={t("performer:accountSettings.pageTitle")}
        breadcrumbs={generateBreadcrumbsData(breadcrumbs, [
          BreadcrumbSection.BackToAccount,
        ])}
      >
        <SettingSection
          sectionHeader={t("performer:accountSettings.myDetails.header")}
          sectionDescription={t(
            "performer:accountSettings.myDetails.description"
          )}
          sectionData={[
            {
              label: t("common:label.name"),
              value: userDetails?.user.fullName ?? "-",
            },
            {
              label: t("common:label.phoneNumber"),
              value: recurlyDetails?.address?.phone ?? "-",
            },
            {
              label: t("common:label.address"),
              value: getAddressValue(),
            },
          ]}
          actionButtonLabel={t("common:button.label.updateMyDetails")}
          actionButtonOnClick={() => navigate(MY_DETAILS_FULL_PATH)}
        />
        <hr />
        <SettingSection
          sectionHeader={t("performer:accountSettings.myLoginDetails.header")}
          sectionDescription={t(
            "performer:accountSettings.myLoginDetails.description"
          )}
          sectionData={[
            {
              label: t("common:label.username"),
              value: userDetails?.user?.username ?? "-",
            },
            {
              label: t("common:label.emailId"),
              value: userDetails?.user?.email ?? "-",
            },
            {
              label: t("common:label.password"),
              value: "********",
            },
            {
              label: t("common:label.securityQuestion"),
              value: userSecurityQ?.question ?? "-",
            },
          ]}
          actionButtonLabel={t("common:button.label.updateMyLogInDetails")}
          actionButtonOnClick={() => navigate(MY_LOGIN_DETAILS_FULL_PATH)}
        />
        <hr />

        {showSubscriptionBlock && (
          <SettingSection
            sectionHeader={t("performer:accountSettings.mySubscription.header")}
            sectionDescription={t(
              "performer:accountSettings.mySubscription.description"
            )}
            sectionData={[
              {
                label: t("common:label.subscriptionType"),
                value: noActiveSubscription
                  ? t("performer:accountSettings.noActiveSubscription")
                  : recurlyDetails?.subscription?.plan,
              },
            ]}
            actionButtonLabel={
              noActiveSubscription
                ? t("common:button.label.reactivateMembership")
                : t("common:button.label.manageSubscription")
            }
            actionButtonOnClick={() => {
              if (noActiveSubscription && !isValidAddress)
                return showUpdateDetailsPopUp();

              if (noActiveSubscription) return navigate(reactivationLink);

              return navigate(MY_SUBSCRIPTION_FULL_PATH);
            }}
            actionButtonHidden={noActiveSubscription}
          />
        )}
      </PageWrapperWithinLayout>
    </>
  );
};

export default AccountSettings;
